import {atoms, Box} from '@dropbox/dig-foundations';
import {pendingDeleteCommentIdsAtom} from 'atoms/comment';
import type {DocumentThread} from 'client';
import {getCommentRenderState} from 'components/DSYS/editor/plugins/comments/commentRenderState';
import {Facepile} from 'components/DSYS/Facepile';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';

const getOrderedCommenterLdaps = (thread: DocumentThread | undefined): string[] => {
  if (!thread) return [];
  const seen = new Set<string>();
  const ordered: string[] = [];
  thread.comments?.forEach((comment) => {
    if (comment.is_deleted) return;
    const ldap = comment.employee?.ldap;
    if (!ldap || seen.has(ldap)) return;
    seen.add(ldap);
    ordered.push(ldap);
  });
  return ordered;
};

export const CommentDisplayPreview = ({
  thread,
  compact = false,
}: {
  thread?: DocumentThread;
  compact?: boolean;
}) => {
  const pendingDeleteCommentIds = useAtomValue(pendingDeleteCommentIdsAtom);

  if (!thread) return null;
  const {latestActiveComment: latestComment, latestPendingDeleteComment} = getCommentRenderState(
    thread.comments,
    pendingDeleteCommentIds
  );
  if (!latestComment && !latestPendingDeleteComment) return null;

  return (
    <Box
      data-testid="comment-display-preview"
      style={{width: '100%', minHeight: 44, overflow: 'hidden', gap: compact ? 0 : 8}}
      display="flex"
      alignItems="center"
      paddingLeft={compact ? '8' : '16'}
      paddingRight={compact ? '8' : '16'}
    >
      <Box flexShrink={0} display="flex" justifyContent="flex-start" style={{marginLeft: '-8px'}}>
        <Facepile
          ldaps={getOrderedCommenterLdaps(thread)}
          size="small"
          overflow={compact ? 2 : 3}
          shouldSort={false}
          hideTooltip
          onClick={() => {
            // no-op. We need to pass in `onClick` so that DIG will use `cursor: pointer` for the facepile
          }}
        />
      </Box>
      {!compact && (
        <Box
          display="flex"
          alignItems="center"
          overflow="hidden"
          className={atoms({color: 'Text Subtle', textAlign: 'left'})}
          style={{flex: 1, minWidth: 0, minHeight: 44}}
        >
          {latestComment ? (
            <RichTextArea
              source="comments"
              value={latestComment.content}
              editable={false}
              hideToolbar
              disableBorder
              minHeight={0}
              truncateLines={2}
            />
          ) : (
            t('deleting')
          )}
        </Box>
      )}
    </Box>
  );
};
